// src/services/ContractService.ts
import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { Contract } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

const contractCollectionRef = collection(db, "contracts");

export const ContractService = {
  async createContract(
    contractData: Omit<
      Contract,
      "id" | "createdAt" | "updatedAt" | "users" | "children"
    >,
  ): Promise<string> {
    const newContract: Omit<Contract, "id"> = {
      ...contractData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const contractRef = await addDoc(contractCollectionRef, clean(newContract));
    return contractRef.id;
  },

  async getContract(contractId: string): Promise<Contract | null> {
    const contractDoc = await getDoc(doc(contractCollectionRef, contractId));
    if (contractDoc.exists()) {
      return { id: contractDoc.id, ...contractDoc.data() } as Contract;
    }
    return null;
  },

  async getAllContracts(accountId: string): Promise<Contract[]> {
    const contractSnapshot = await getDocs(
      query(contractCollectionRef, where("accountId", "==", accountId)),
    );
    return contractSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Contract,
    );
  },

  async updateContract(
    contractId: string,
    updatedData: Partial<Omit<Contract, "id" | "createdAt">>,
  ): Promise<void> {
    const contractDocRef = doc(contractCollectionRef, contractId);
    await updateDoc(contractDocRef, clean({
      ...updatedData,
      updatedAt: Timestamp.now(),
    }));
  },

  async deleteContract(contractId: string): Promise<void> {
    const contractDocRef = doc(contractCollectionRef, contractId);
    await deleteDoc(contractDocRef);
  },
};
